/**
 * Thin axios wrapper around the admin-only data-changes endpoints
 * shipped by the diff-loader work, all under `/admin/data-changes/*`.
 *
 * The endpoints surface what shifted in the voter roll between
 * snapshots — added voters, removed voters, modified fields, and
 * newly-introduced elections — and require ADMIN role. Tenant scope
 * is enforced server-side by the JWT middleware.
 *
 * Pattern mirrors `src/api/analytics.ts`: bare `client.get` + typed
 * return values + async/await. No mock fixtures here yet — the
 * backend returns empty results until the diff loader populates the
 * deltas table, and the view handles empty states gracefully.
 */
import client from '@/api/client';

// ---------------------------------------------------------------------------
// Shared envelope shape used by paginated list endpoints.
// Mirrors `src/api/feedback.ts` `ListAARFeedbackResponse` but generic.
// ---------------------------------------------------------------------------

export interface ListEnvelope<T> {
  items: T[];
  page: number;
  page_size: number;
  total: number;
  sort?: string | null;
  order?: 'asc' | 'desc' | null;
  applied_filters?:
    | Record<string, unknown>
    | Array<{ key: string; label: string; value: string }>;
}

// ---------------------------------------------------------------------------
// Response schemas — keep in sync with backend Pydantic models.
// ---------------------------------------------------------------------------

export interface ImportBatchRead {
  import_batch_id: string;
  observed_at: string; // ISO datetime
  total_deltas: number;
  voters_added: number;
  voters_removed: number;
  voters_modified: number;
  elections_added: number;
}

export interface TopFieldRollup {
  field_name: string;
  count: number;
}

export interface ChangeSummaryRead {
  import_batch_id: string;
  observed_at: string;
  voters_added: number;
  voters_removed: number;
  voters_modified: number;
  elections_added: number;
  top_fields: TopFieldRollup[];
}

export interface TransitionRead {
  from_value: string;
  to_value: string;
  count: number;
  change_reason: string | null;
}

export interface ElectionAddedRead {
  election_date: string; // ISO date
  election_type: string;
  voter_count: number;
}

export type DeltaKind = 'added' | 'removed' | 'modified' | 'election_added';

export interface VoterDeltaRead {
  id: number;
  voter_id: number;
  voter_id_external: string;
  voter_name: string;
  delta_kind: DeltaKind;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  change_reason: string | null;
  observed_at: string; // ISO datetime
}

// ---------------------------------------------------------------------------
// 1. Imports list — GET /admin/data-changes/imports
// ---------------------------------------------------------------------------

export async function fetchImportBatches(): Promise<ImportBatchRead[]> {
  const res = await client.get<ImportBatchRead[]>(
    '/admin/data-changes/imports',
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// 2. Summary — GET /admin/data-changes/summary
// Omitted import_batch_id defaults to the most-recent batch.
// ---------------------------------------------------------------------------

export async function fetchChangeSummary(
  importBatchId?: string,
): Promise<ChangeSummaryRead> {
  const params: Record<string, string> = {};
  if (importBatchId) params.import_batch_id = importBatchId;
  const res = await client.get<ChangeSummaryRead>(
    '/admin/data-changes/summary',
    { params },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// 3. Transitions — GET /admin/data-changes/transitions
// `field` is required ('status' | 'party'). import_batch_id optional.
// ---------------------------------------------------------------------------

export type TransitionField = 'status' | 'party';

export async function fetchTransitions(
  field: TransitionField,
  importBatchId?: string,
): Promise<TransitionRead[]> {
  const params: Record<string, string> = { field };
  if (importBatchId) params.import_batch_id = importBatchId;
  const res = await client.get<TransitionRead[]>(
    '/admin/data-changes/transitions',
    { params },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// 4. Elections added — GET /admin/data-changes/elections-added
// ---------------------------------------------------------------------------

export async function fetchElectionsAdded(
  importBatchId?: string,
): Promise<ElectionAddedRead[]> {
  const params: Record<string, string> = {};
  if (importBatchId) params.import_batch_id = importBatchId;
  const res = await client.get<ElectionAddedRead[]>(
    '/admin/data-changes/elections-added',
    { params },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// 5. Voter deltas — GET /admin/data-changes/voters
// Paginated. Filters: delta_kind, field_name. import_batch_id optional.
// ---------------------------------------------------------------------------

export type VoterDeltaSortKey =
  | 'voter_name'
  | 'field_name'
  | 'delta_kind'
  | 'observed_at';

export interface FetchVoterDeltasOptions {
  importBatchId?: string;
  deltaKind?: DeltaKind | DeltaKind[];
  fieldName?: string | string[];
  q?: string;
  sort?: VoterDeltaSortKey;
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

/**
 * Build the querystring object the backend expects. Centralized so the
 * paginated list call AND the CSV streaming download stay in sync — if
 * one knows about a filter the other has to as well.
 */
export function buildVoterDeltaParams(
  opts: FetchVoterDeltasOptions,
): Record<string, string | number> {
  const params: Record<string, string | number> = {
    page: opts.page ?? 1,
    page_size: opts.pageSize ?? 50,
  };
  if (opts.importBatchId) params.import_batch_id = opts.importBatchId;
  if (opts.deltaKind) {
    params.delta_kind = Array.isArray(opts.deltaKind)
      ? opts.deltaKind.join(',')
      : opts.deltaKind;
  }
  if (opts.fieldName) {
    params.field_name = Array.isArray(opts.fieldName)
      ? opts.fieldName.join(',')
      : opts.fieldName;
  }
  if (opts.q && opts.q.trim().length > 0) params.q = opts.q.trim();
  if (opts.sort) params.sort = opts.sort;
  if (opts.order) params.order = opts.order;
  return params;
}

export async function fetchVoterDeltas(
  opts: FetchVoterDeltasOptions = {},
): Promise<ListEnvelope<VoterDeltaRead>> {
  const params = buildVoterDeltaParams(opts);
  const res = await client.get<ListEnvelope<VoterDeltaRead>>(
    '/admin/data-changes/voters',
    { params },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// 6. Per-voter history — GET /admin/data-changes/voters/by-external-id/{id}
// Returns the full delta history for a single external voter id, across
// every batch. When the voter is missing/removed there will still be rows
// describing the removal — empty array means "no record at all".
// ---------------------------------------------------------------------------

export async function fetchVoterDeltaHistory(
  voterIdExternal: string,
): Promise<VoterDeltaRead[]> {
  const res = await client.get<VoterDeltaRead[]>(
    `/admin/data-changes/voters/by-external-id/${encodeURIComponent(voterIdExternal)}`,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// 7. Cross-batch compare — GET /admin/data-changes/compare
// ---------------------------------------------------------------------------

export interface BatchComparisonFieldDelta {
  field_name: string;
  from_count: number;
  to_count: number;
  delta: number;
}

export interface BatchComparison {
  from_batch: ImportBatchRead;
  to_batch: ImportBatchRead;
  delta: {
    voters_added: number;
    voters_removed: number;
    voters_modified: number;
    elections_added: number;
  };
  field_deltas: BatchComparisonFieldDelta[];
}

export async function fetchBatchComparison(
  fromBatch: string,
  toBatch: string,
): Promise<BatchComparison> {
  const res = await client.get<BatchComparison>(
    '/admin/data-changes/compare',
    { params: { from_batch: fromBatch, to_batch: toBatch } },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// 8. CSV export — GET /admin/data-changes/voters.csv
// Streams a CSV download. Same filter shape as fetchVoterDeltas. Returns
// the raw Blob so the caller can trigger a browser download. Bubbles up
// 413 (too-many-rows) so the caller can show a "narrow your filters"
// message — no automatic chunking, deliberately.
// ---------------------------------------------------------------------------

export async function fetchVoterDeltasCsv(
  opts: FetchVoterDeltasOptions = {},
): Promise<Blob> {
  const params = buildVoterDeltaParams(opts);
  // page/page_size are nonsense for streaming CSV; the server may ignore
  // them but stripping keeps the URL tidy and the request log readable.
  delete params.page;
  delete params.page_size;
  const res = await client.get<Blob>('/admin/data-changes/voters.csv', {
    params,
    responseType: 'blob',
  });
  return res.data;
}
